import Constants from 'expo-constants';
import { Platform } from 'react-native';

import { InsiderLanguageMap, InsiderServiceConfig } from 'app/services/Insider.types';
import Logger from 'app/services/Logger';

const eventQueue: { (): void }[] = [];

const reinitializeInsiderManager = () => {
  if (typeof window?.Insider?.eventManager?.dispatch === 'function') {
    window.Insider.eventManager.dispatch('init-manager:re-initialize');
  }
};

const safeRequest = (callback: () => void) => {
  if (!window.Insider) {
    console.log('Insider SDK not loaded yet, queuing event');
    eventQueue.push(callback);
  } else {
    console.log('Insider SDK loaded, processing event');
    callback();
  }
};

const InsiderServiceWeb: InsiderServiceConfig = {
  init() {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return;

    // disable Insider in SDET environment to prevent permission requests
    if (Constants.expoConfig?.extra?.sdet === 'true') {
      return;
    }

    Logger.debug('Initializing Insider SDK');

    window.insider_object = window.insider_object || {};
  },

  getUser() {
    return window.insider_object?.user || {};
  },

  async handleModifyUser({ userID, email, locale }) {
    safeRequest(() => {
      const payload: { language: string; email?: string; uuid?: string } = {
        language: InsiderLanguageMap[locale] || 'en_US',
      };

      if (email && userID) {
        payload.email = email;
        payload.uuid = userID;
      }

      window.insider_object.user = { ...this.getUser(), ...payload };

      reinitializeInsiderManager();
    });
  },

  handleHomePageView() {
    this.handleEvent({ type: 'page', value: 'Home' });
  },

  handleListingPageView(taxonomy, params) {
    this.handleEvent(
      {
        type: 'listing',
        value: {
          items: [
            {
              id: params.product_id,
              name: params.page_name,
              taxonomy,
              currency: 'USD',
              unit_price: 1.0,
              unit_sale_price: 1.0,
              url: window.location.href || '',
              product_image_url: params.image_url,
            },
          ],
        },
      },
      { product_id: params.product_id }
    );
  },

  handleProductPageView(product) {
    this.handleEvent({
      type: 'product',
      value: {
        id: product.productID,
        name: product.name,
        taxonomy: product.taxonomy,
        unit_price: product.price,
        unit_sale_price: product.price,
        product_image_url: product.imageURL,
        currency: product.currency,
        locale: product.locale,
        url: window.location.href || '',
      },
    });
  },

  handleAttractionPageView(params) {
    this.handleEvent({
      type: 'events',
      value: {
        event_name: 'attraction_page_view',
        event_params: { product_id: params.attraction_key, name: params.page_name },
      },
    });
  },

  handleOffersPageView(params) {
    this.handleEvent({
      type: 'events',
      value: {
        event_name: 'offer_page_view',
        event_params: { product_id: params.product_id, name: params.page_name },
      },
    });
  },

  handleEvent(eventOrName, params = {}) {
    safeRequest(() => {
      if (typeof eventOrName !== 'object') return;

      const { type, value } = eventOrName;

      if (type === 'events') {
        window.Insider?.track(type, [value as Record<string, unknown>]);
      } else {
        const customData = Object.keys(params).length > 0 ? { custom: params } : {};
        if (!window.insider_object) {
          window.insider_object = window.insider_object ?? {};
        }
        window.insider_object[type] =
          typeof value === 'object' && !Array.isArray(value)
            ? { ...value, ...customData }
            : {
                type: value,
                ...customData,
              };
      }

      reinitializeInsiderManager();
    });
  },

  flushQueue() {
    if (!window.Insider) {
      return;
    }

    while (eventQueue.length > 0) {
      const callback = eventQueue.shift();
      if (callback) {
        callback();
      }
    }
  },
};

export default InsiderServiceWeb;
